/**
 * Shared formatters for CRM blocks. Keep small and pure so server-rendered
 * pages and client components both consume them.
 */

import { formatCompact } from "../../format";

export function formatMoneyMinor(minor: number, currency: string): string {
  const major = minor / 100;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(major);
  } catch {
    return `${minor} ${currency}`;
  }
}

/** The locale currency symbol ("$", "€", "£", …), or the code as a fallback. */
function currencySymbol(currency: string): string {
  try {
    return (
      new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 })
        .formatToParts(0)
        .find((p) => p.type === "currency")?.value ?? currency
    );
  } catch {
    return currency;
  }
}

/**
 * Compact, currency-aware money from MINOR units — "$96.8k" / "€96.8k". Pairs
 * the locale currency symbol with the shared `formatCompact` magnitude so the
 * compact design holds for any currency (not just USD).
 */
export function formatMoneyCompact(minor: number, currency: string): string {
  return `${currencySymbol(currency)}${formatCompact(minor / 100)}`;
}

// Relative time is now a single source of truth — re-exported from the shared
// formatter (packages/ui/src/format.ts) so CRM call sites don't drift.
export { formatRelativeTime } from "../../format";

export function dayBucket(iso: string): string {
  return iso.slice(0, 10);
}
